import React, { memo, useCallback, useDeferredValue, useEffect, useMemo } from "react";
import * as io from "io-ts";
import cx from "classnames";
import { css } from "@linaria/core";
import { blockHeader } from "../_styles/BlockHeader";
import { AnalysisSelector } from "./AnalysisSelector";
import { BlockLoading } from "../Loading";
import { colors, spacings } from "@ui/Theme";
import { ImageViewer } from "@ui/ImageViewer";
import type { BlockDiagramModelT } from "../../../../../schema/block/diagram";
import {
  DiagramAnnotation,
  DiagramAnnotationEmpty,
  DiagramAnnotationInfo,
  DiagramAnnotationValue,
} from "./Annotation";
import type { Project } from "../../../../../../../../api/useProjects";
import t from "../../../../../../../../i18n";
import { useDashboardEditorContext } from "../../../../../hook/context/editor";
import { MTS_ENV } from "../../../../../../../../lib/setupEnv";
import { useSelectedDiagramAnnotation } from "../../hook/useSelectedDiagramAnnotation";
import BlankStateV2 from "@ui/BlankStateV2";
import { NoData } from "@ui/Illustration/react";
import { hashCode } from "diagnostics-lib/object";
import { groupBy } from "diagnostics-lib/iterable";
import createGlobalState from "@ui/lib/createGlobalState";
import { DiagramMenu } from "./Menu";
import { useAnnotationDisplay } from "./Annotation/hook/useAnnotationDisplay";
import {
  AGGREGATE_NO_SYSTEM,
  useAvailableResources,
} from "../../../../../hook/useAvailableResources";
import { DiagramLegend } from "./DiagramLegend";
import type { Dataset } from "../../../../../hook/useDataset";
import { getAvailableResourceKind } from "../../../../../utils/getAvailableResourceKind";
import { useAnnotations, type Annotation } from "./Annotation/hook/useAnnotations";
import { getThresholdValue } from "./thresholdValue";
import { Text, H3 } from "@ui/Text";
import { FullViewportTrigger } from "@ui/FullViewportButton";
import { ExportWidgetButton } from "@ui/ExportWidgetButton";
import { generateExportFileName } from "diagnostics-lib/string";
import { Duration, Period, Range } from "../../../../../schema/period";
import { Calendar2Line } from "@ui/IconV2/react";
import { locale } from "../../../../../../../../i18n/locale";
import { useDashboard } from "../../../../../hook/api/dashboard";
import {
  AggregationPeriod,
  aggregationPeriodLabel,
} from "../../../../../../../../domain/aggregation/period";

import { DashboardDatasetLoader } from "../../../../../component/DatasetLoader";

const useExpandedAnnotationState = createGlobalState({
  storage: "local",
  key: "ExpandedAnnotationState",
  type: io.array(io.boolean),
  scoped: true,
});

const useRolesDisplayState = createGlobalState({
  storage: "local",
  key: "RolesDisplayState",
  type: io.union([io.boolean, io.undefined]),
  scoped: true,
  defaultValue: undefined,
});

type BlockDiagramProps = {
  project: Project;
  block: BlockDiagramModelT;
  allowFullscreen?: boolean;
  editable?: boolean;
  className?: string;
};

export const BlockDiagram = memo((props: BlockDiagramProps) => (
  <DashboardDatasetLoader block={props.block} projectId={props.project.id}>
    {dataset => <BlockDiagramReady {...props} dataset={dataset} />}
  </DashboardDatasetLoader>
));

const BlockDiagramReady = memo((props: BlockDiagramProps & { dataset: Dataset }) => {
  const { dataset } = props;
  const scope = `${props.project.id}:${props.block.id}`;
  const [readyScope, setReadyScope] = React.useState<string | null>(null);

  if (!dataset.pending && readyScope !== scope) {
    setReadyScope(scope);
  }

  if (dataset.pending && readyScope !== scope && props.block.config.url.length > 0) {
    return (
      <div className={cx(props.className, "printable-diagram", imageHolderCss)}>
        <BlockLoading />
      </div>
    );
  }

  return <BlockDiagramContent key={scope} {...props} dataset={dataset} />;
});

const BlockDiagramContent = memo(
  ({
    project,
    block,
    editable = false,
    allowFullscreen = false,
    className,
    dataset,
  }: BlockDiagramProps & { dataset: Dataset }) => {
    const editor = useDashboardEditorContext();
    const { dashboard } = useDashboard(project.id, block.dashboardId);
    const availableResources = useAvailableResources({ projectId: project.id });
    const { quantities, faults } = dataset;
    const [selectedAnnotationIndex, selectAnnotationIndex] = useSelectedDiagramAnnotation({
      scope: block.id,
    });
    const nextAnnotations = useAnnotations(project, block, quantities, faults);
    const [readyAnnotations, setReadyAnnotations] = React.useState(nextAnnotations);

    // Streaming datasets are empty; preserve the last completed annotations during refresh.
    if (!dataset.pending && readyAnnotations !== nextAnnotations) {
      setReadyAnnotations(nextAnnotations);
    }

    const annotations = useDeferredValue(dataset.pending ? readyAnnotations : nextAnnotations);

    const { displayedAnnotations, annotationsDisplay, setAnnotationsDisplay } =
      useAnnotationDisplay(block.id, annotations, { displayAll: editable });

    const resourceKindsUsed = React.useMemo(
      () =>
        block.config.layers.reduce<Set<string>>((acc, { encodings }) => {
          const identifier = encodings[0]?.identifier;
          if (!identifier) return acc;
          const kind = getAvailableResourceKind(availableResources, identifier);
          if (!kind) return acc;

          acc.add(kind);
          return acc;
        }, new Set()),
      [availableResources, block.config.layers],
    );

    const updateViewportConfig = (viewportConfig: {
      scale: number;
      centerOffset: { x: number; y: number };
    }) => {
      if (!block) return;
      void editor?.updateBlock({
        ...block,
        config: {
          ...block.config,
          viewport: {
            ...block.config.viewport,
            scale: viewportConfig.scale,
            centerOffset: { ...viewportConfig.centerOffset },
          },
        },
      });
    };

    const updateAnnotationPosition = useCallback(
      (position: [number, number, number, number], index: number) => {
        if (!block) return;
        void editor?.updateBlock({
          ...block,
          config: {
            ...block.config,
            layers: block.config.layers.map((layer, j) => {
              if (index === j) {
                return { ...layer, position };
              }
              return layer;
            }),
          },
        });
      },
      [editor, block],
    );

    const updateAnnotationAnchorRelativePosition = useCallback(
      (position: [number, number], index: number) => {
        if (!block) return;
        void editor?.updateBlock({
          ...block,
          config: {
            ...block.config,
            layers: block.config.layers.map((layer, j) => {
              if (index === j) {
                return { ...layer, anchorRelativePositions: position };
              }
              return layer;
            }),
          },
        });
      },
      [editor, block],
    );

    /** @note temporary solution, extra step require statuses & validities from data in conjunction with block menu*/
    const defaultExpandedAnnotations = useMemo(
      () => Array.from({ length: annotations.length }, () => block.config.show_all ?? true),
      [annotations.length, block.config.show_all],
    );
    const [expandedAnnotations, expandAnnotations] = useExpandedAnnotationState({
      scope: block.id,
      defaultValue: defaultExpandedAnnotations,
    });

    const [isRoleDisplayedSettings, setIsRoleDisplayedSettings] = useRolesDisplayState({
      scope: block.id,
    });

    const isRoleDisplayed =
      isRoleDisplayedSettings === undefined ? block.config.show_role : isRoleDisplayedSettings;

    const handleAnnotationExpension = useCallback(
      (i: number) =>
        expandAnnotations(state => {
          if (i >= state.length) return [...state, false]; /** @note new added */
          return state.map((b, j) => (i === j ? !b : b));
        }),
      [expandAnnotations],
    );

    useEffect(() => {
      if (!editable) return;

      expandAnnotations(Array.from({ length: annotations.length }, () => true));

      setAnnotationsDisplay({
        validity: [
          "VALID_AUTO",
          "VALID_MANUAL",
          "VALID_PARTIAL_INPUTS",
          "INVALID_AUTO",
          "INVALID_MANUAL",
          "INVALID_AUTO_CROSS_FILTER",
          "INVALID_INHERITED",
        ],
        faultStatus: [
          "TO_BE_ANALYSED",
          "SUSPECTED",
          "CONFIRMED",
          "REJECTED",
          "NON_MODELLED",
          "NOT_DETECTED",
        ],
      });
    }, [editable, annotations.length, expandAnnotations, setAnnotationsDisplay]);

    return (
      <div className={cx(className, "printable-diagram", imageHolderCss)}>
        <header className={cx(blockHeader, diagramHeader)}>
          {block.data.title && <H3>{block.data.title}</H3>}
          <div className={diagramHeaderNavigation}>
            {Period.is(block.data.period) && block.config.aggregate ? (
              <Text type="body2SemiBold" className={diagramPeriod}>
                <Calendar2Line />
                {Range.is(block.data.period) && (
                  <>
                    {new Date(block.data.period.from as number).toLocaleDateString(locale)} -{" "}
                    {new Date(block.data.period.to as number).toLocaleDateString(locale)}
                  </>
                )}
                {Duration.is(block.data.period) &&
                  AggregationPeriod.is(block.data.period) &&
                  aggregationPeriodLabel(block.data.period)}
              </Text>
            ) : (
              <AnalysisSelector block={block} project={project} />
            )}
            {!editable && (
              <ExportWidgetButton
                fileName={generateExportFileName({
                  filename:
                    typeof block.data !== "string" && block.data.title ? block.data.title : "",
                  projectName: dashboard?.title + "_" + project.name,
                  extension: "png",
                })}
              />
            )}
            {allowFullscreen && !editable && <FullViewportTrigger />}
          </div>
        </header>
        {block.config.url.length > 0 ? (
          <>
            <ImageViewer
              src={`${MTS_ENV.COREV2_ASSETS_URL}/${block.config.url}`}
              alt={block.data.title ?? ""}
              className={diagramViewer}
              onViewportChange={editable ? updateViewportConfig : undefined}
              viewportConfig={block.config.viewport ?? undefined}
              controls
              menu={
                <DiagramMenu
                  expandAnnotations={expandAnnotations}
                  expandedAnnotations={expandedAnnotations}
                  annotationsDisplay={annotationsDisplay}
                  displayAnnotations={setAnnotationsDisplay}
                  isRoleDisplayed={isRoleDisplayed}
                  setIsRoleDisplayed={setIsRoleDisplayedSettings}
                  resourceKindsUsed={Array.from(resourceKindsUsed)}
                />
              }
            >
              {annotations.map((annotation, i) => (
                <DiagramAnnotation
                  projectId={project.id}
                  key={annotation.id}
                  index={i}
                  expanded={expandedAnnotations[i] ?? true}
                  position={annotation.position as [number, number, number, number]}
                  anchorRelativePositions={annotation.anchorRelativePositions as [number, number]}
                  anchor={annotation?.anchor}
                  selected={selectedAnnotationIndex === i}
                  visible={Boolean(displayedAnnotations[i])}
                  annotation={annotation}
                  isRoleDisplayed={isRoleDisplayed}
                  isDashboardEditing={editable}
                  onPositionChange={editable ? updateAnnotationPosition : undefined}
                  onRelativePositionChange={
                    editable ? updateAnnotationAnchorRelativePosition : undefined
                  }
                  onPointerDown={selectAnnotationIndex}
                  onExpansionChange={() => handleAnnotationExpension(i)}
                >
                  <DiagramAnnotationInfo>
                    {hover =>
                      annotation.showLabel || hover
                        ? annotation.type === "SYSTEM" &&
                          annotation.encodings[0]?.identifier === AGGREGATE_NO_SYSTEM
                          ? ""
                          : annotation.showDescription || hover
                            ? (annotation.description ?? "")
                            : null
                        : null
                    }
                  </DiagramAnnotationInfo>

                  <DiagramAnnotationValues
                    projectId={project.id}
                    showPartLoadSensitiveKpis={project.showPartLoadSensitiveKpis}
                    aggregated={Boolean(block.config.aggregate)}
                    annotation={annotation}
                    expanded={expandedAnnotations[i] ?? true}
                  />
                  {editable && annotation.values.length === 0 && (
                    <DiagramAnnotationInfo>
                      {t("project.dashboard.block.diagram.no_data")}
                    </DiagramAnnotationInfo>
                  )}
                </DiagramAnnotation>
              ))}
            </ImageViewer>
            <DiagramLegend
              projectId={project.id}
              annotations={annotations}
              isRoleDisplayed={isRoleDisplayed}
            />
          </>
        ) : (
          <BlankStateV2
            picto={<NoData size="m" />}
            title={t("project.dashboard.block.diagram.blank.title")}
            subtitle={editable ? t("project.dashboard.block.diagram.blank.subtitle") : ""}
          />
        )}
      </div>
    );
  },
);

const DiagramAnnotationValues = memo(
  ({
    projectId,
    annotation,
    expanded,
    aggregated,
    showPartLoadSensitiveKpis,
  }: {
    projectId: string;
    annotation: Annotation;
    expanded: boolean;
    aggregated: boolean;
    showPartLoadSensitiveKpis: boolean;
  }) => {
    const selectedIdentifier = annotation?.encodings?.[0]?.identifier ?? null;
    const availableResources = useAvailableResources({ projectId });

    const isSelectedIdentifierUsedForSimulationOutputs = useMemo(
      () =>
        Boolean(
          availableResources.quantity.identifiers.find(
            identifier =>
              selectedIdentifier === identifier.name && identifier.usedForSimulationOutputs,
          ),
        ),
      [availableResources.quantity.identifiers, selectedIdentifier],
    );

    const status = useMemo(
      () =>
        annotation.values
          .find(e => e.column === "faultStatus" || e.column === "validity")
          ?.value?.toString(),
      [annotation.values],
    );

    const values = useMemo(
      () =>
        annotation.values.filter(({ column }) => {
          if (column === "validity" || column === "faultStatus") return false;
          if (
            !isSelectedIdentifierUsedForSimulationOutputs &&
            (column === "expected" || column === "gap")
          )
            return false;
          return true;
        }),
      [annotation.values, isSelectedIdentifierUsedForSimulationOutputs],
    );

    const thresholds = useMemo(
      () => groupBy(annotation.thresholds, item => `${item.column}-${item.quantity}`),
      [annotation.thresholds],
    );

    const getThresholdValueForEntry = useCallback(
      (entry: Annotation["values"][number]) =>
        getThresholdValue(
          entry,
          entry.column === "gap" && thresholds.has(`normalizedGap-${entry.quantity}`),
        ),
      [thresholds],
    );

    const getThresholds = useCallback(
      (entry: Annotation["values"][number]) =>
        (entry.column === "gap" && thresholds.has(`normalizedGap-${entry.quantity}`)
          ? thresholds.get(`normalizedGap-${entry.quantity}`)
          : thresholds.get(`${entry.column}-${entry.quantity}`)) ?? [],
      [thresholds],
    );

    if (
      expanded &&
      annotation.type === "FAULT" &&
      (status === "REJECTED" || status === "LEGACY_REJECTED")
    ) {
      return (
        <DiagramAnnotationEmpty>
          {t("project.dashboard.block.diagram.false_detection")}
        </DiagramAnnotationEmpty>
      );
    }

    if (expanded && annotation.type === "FAULT" && values.every(({ value }) => value === null)) {
      return (
        <DiagramAnnotationEmpty>
          {t("project.dashboard.block.diagram.fault_not_detected")}
        </DiagramAnnotationEmpty>
      );
    }
    const singleGapValue =
      values.filter(v => v.column === "gap" || v.column === "normalizedGap").length === 1;

    return (
      <>
        {values.map((value, index) => {
          const nextValue = values[index + 1];
          const hideBottomBorder =
            (value.column === "measured" && nextValue?.column === "expected") ||
            (value.column === "gap" && nextValue?.column === "normalizedGap");

          const hidePartLoadSensitiveKpisFlag =
            !showPartLoadSensitiveKpis && value.column === "systemImpact";
          if (hidePartLoadSensitiveKpisFlag) {
            return null;
          }

          return (
            <DiagramAnnotationValue
              projectId={projectId}
              aggregated={aggregated}
              key={hashCode(value)}
              annotationType={annotation.type}
              annotationValue={value}
              thresholdValue={getThresholdValueForEntry(value)}
              thresholds={getThresholds(value)}
              showPartLoadSensitiveKpis={showPartLoadSensitiveKpis}
              hasSingleAnnotations={values.length === 1}
              hideBottomBorder={hideBottomBorder}
              singleGapValue={singleGapValue}
            />
          );
        })}
      </>
    );
  },
);
const imageHolderCss = css`
  overflow: hidden;
`;

const diagramHeader = css`
  display: grid;
  grid-template-columns: 1fr max-content;
  align-items: start;
  margin-bottom: ${spacings.m};

  h3 {
    grid-column: 1 / span 1;
    grid-row: 1 / span 1;
  }
`;

const diagramHeaderNavigation = css`
  display: flex;
  grid-column: 2 / span 1;
  align-items: center;
  gap: ${spacings.xs};
`;

const diagramPeriod = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
  color: ${colors.carbon800};
`;

const diagramViewer = css`
  width: 100%;
  min-height: 600px;
  flex: 1 1 auto;
`;
